import React, { useEffect, useState } from "react";
import api from "../Service/api";
import "./Monthly.css";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useNavigate } from "react-router-dom";

const Monthly = () => {
  const navigate = useNavigate();
  const today = new Date();

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [productSales, setProductSales] = useState([]);
  const [customerPurchases, setCustomerPurchases] = useState([]);
  const [monthlySales, setMonthlySales] = useState([]);
    const [monthlyFactorySalesGraph, setMonthlyFactorySalesGraph] = useState([]);

  const [saleComparison, setSaleComparison] = useState("");
    const [factorySaleComparison, setfactorySaleComparison] = useState("");

  const [activeOrders, setActiveOrders] = useState([]); // For not in process and in process orders

  const [summary, setSummary] = useState({
    inventoryValue: 0,
    companyBalance: 0,
    customerBalance: 0,
    expenses: 0,
    companyPaidAmount: 0,
    customerPaidAmount: 0,
    salary: 0,
    product: 0,
    sale: 0,
    grossProfit: 0,
    netProfit: 0,
  });

  // ✅ New state to prevent flash
  const [authorized, setAuthorized] = useState(false);

  const fetchMonthlySummary = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/inventory/monthly", {
        params: { year, month },
      });

      setSummary(res.data);
      setProductSales(res.data.topProducts || []);
      setCustomerPurchases(res.data.topCustomers || []);
      setMonthlySales(res.data.monthlySalesGraph || []);
      setMonthlyFactorySalesGraph(res.data.monthlyFactorySalesGraph || []);

      setSaleComparison(res.data.saleComparison || "");
      setfactorySaleComparison(res.data.factorySaleComparison || "");
    } catch (err) {
      console.error(err);
      alert("Failed to load monthly summary");
    } finally {
      setLoading(false);
    }
  };

  // Fetch active orders (not in process and in process)
  const fetchActiveOrders = async () => {
    setOrdersLoading(true);
    try {
      const response = await api.get("/api/orders/incomplete");
      setActiveOrders(response.data || []);
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Update order status
  const updateOrderStatus = async (id, currentStatus) => {
    let newStatus;
    if (currentStatus === 'not in process') {
      newStatus = 'in process';
    } else if (currentStatus === 'in process') {
      newStatus = 'done';
    } else {
      return;
    }

    try {
      await api.post('/api/orders', { id, status: newStatus });
      // Refresh the orders list
      await fetchActiveOrders();
    } catch (err) {
      console.error("Error updating order status:", err);
      alert("Failed to update order status");
    }
  };

  // Delete order (soft delete)
  const deleteOrder = async (id) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        await api.delete(`/api/orders/${id}`);
        // Refresh the orders list
        await fetchActiveOrders();
      } catch (err) {
        console.error("Error deleting order:", err);
        alert("Failed to delete order");
      }
    }
  };

  const formatCurrency = (amount) => {
    const value = amount || 0;
    const color = value < 0 ? "red" : "black";
    return <span style={{ color }}>{value}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString();
  };

  // 🔥 Check authority before rendering
  useEffect(() => {
    const storedAuthorities = localStorage.getItem("authorities");
    const authorities = storedAuthorities ? JSON.parse(storedAuthorities) : [];

    if (authorities.includes("HOME")) {
      setAuthorized(true);
    } else {
      navigate("/dashboard", { replace: true }); // redirect silently
    }
  }, [navigate]);

  // 🔥 Load data only if authorized
  useEffect(() => {
    if (authorized) {
      fetchMonthlySummary();
      fetchActiveOrders();
    }
  }, [authorized]);

  // ✅ Don't render anything if not authorized yet
  if (!authorized) return null;

  return (
    <div className="monthly-container">
      <div className="monthly-header">
        <h2>📊 Monthly Dashboard</h2>

        <div className="filters">
          <select value={year} onChange={(e) => setYear(+e.target.value)}>
            {Array.from({ length: 10 }).map((_, i) => {
              const y = today.getFullYear() - i;
              return (
                <option key={y} value={y}>
                  {y}
                </option>
              );
            })}
          </select>

          <select value={month} onChange={(e) => setMonth(+e.target.value)}>
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>

          <button onClick={fetchMonthlySummary}>{loading ? "Loading..." : "Search"}</button>
        </div>
      </div>

      {/* Active Orders Table Section */}
      <div className="orders-section">
        <h3 className="orders-section-title">
          🚧 Pending Orders {ordersLoading && <span className="orders-loading">(Loading...)</span>}
        </h3>
        
        {ordersLoading ? (
          <div className="orders-loading-message">Loading orders...</div>
        ) : (
          <div className="orders-table-wrapper">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Customer</th>
                  <th>Requirement</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeOrders.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="orders-empty">
                      No pending orders found
                    </td>
                  </tr>
                ) : (
                  activeOrders.map((order) => (
                    <tr key={order.id}>
                      <td>{order.companyName}</td>
                      <td>{order.productName}</td>
                      <td>{order.quantity}</td>
                      <td>{order.customerName}</td>
                      <td>{order.requirement}</td>
                      <td>
                        <span className={`order-status status-${order.status.replace(/\s+/g, '')}`}>
                          {order.status}
                        </span>
                      </td>
                      <td>{formatDate(order.createdAt)}</td>
                      <td>
                        <div className="order-actions">
                          {order.status === 'not in process' && (
                            <button
                              className="order-btn process-btn"
                              onClick={() => updateOrderStatus(order.id, order.status)}
                            >
                              Process
                            </button>
                          )}
                          {order.status === 'in process' && (
                            <button
                              className="order-btn done-btn"
                              onClick={() => updateOrderStatus(order.id, order.status)}
                            >
                              Done
                            </button>
                          )}
                          <button
                            className="order-btn delete-btn"
                            onClick={() => deleteOrder(order.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="monthly-cards">
        <Card title="📦 Inventory Value" value={summary.inventoryValue} />
        <Card title="🏭 Company Balance" value={summary.companyBalance} />
        <Card title="👤 Customer Balance" value={summary.customerBalance} />
        <Card title="💸 Expenses" value={summary.expenses} />
        <Card title="💰 Company Paid" value={summary.companyPaidAmount} />
        <Card title="💵 Customer Paid" value={summary.customerPaidAmount} />
        <Card title="👨‍💼 Salary Amount" value={summary.salary} />
        <Card title="🛒 Purchase Amount" value={summary.product} />
        <Card title="🛍️ Sale Amount" value={summary.sale} />
        <Card title="🛍️ Gross Profit" value={formatCurrency(summary.grossProfit)} />
        <Card title="🛍️ Net Profit" value={formatCurrency(summary.netProfit)} />
      </div>

      <div className="charts-container">
        {/* Top 10 Products */}
        <div className="chart-card">
          <h4>Top Products Sold</h4>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={productSales.slice(0, 10)}
              margin={{ top: 20, right: 20, left: 10, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="productName"
                interval={0}
                angle={-30}
                textAnchor="end"
                height={80}
              />
              <YAxis />
              <Tooltip />
              <Bar dataKey="totalSale" fill="#16a34a" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top 10 Customers */}
        <div className="chart-card">
          <h4>Top Customers</h4>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={customerPurchases.slice(0, 10)}
              margin={{ top: 20, right: 20, left: 10, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="customerName"
                interval={0}
                angle={-30}
                textAnchor="end"
                height={80}
              />
              <YAxis />
              <Tooltip />
              <Bar dataKey="totalPurchase" fill="#22c55e" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h4>📈 Monthly Sales</h4>

          <p
            style={{
              fontWeight: "bold",
              color: saleComparison === "UP" ? "green" : "red",
              marginBottom: "10px",
            }}
          >
            {saleComparison === "UP"
              ? "⬆ Current month sales are higher than last month"
              : "⬇ Current month sales are lower than last month"}
          </p>

          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={monthlySales}
              margin={{ top: 20, right: 20, left: 10, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                interval={0}
                angle={-35}
                textAnchor="end"
                height={60}
              />
              <YAxis />
              <Tooltip />
              <Bar dataKey="totalSale" fill="#2563eb" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

 <div className="chart-card">
          <h4>📈 Monthly Factory Use Amount</h4>

          <p
            style={{
              fontWeight: "bold",
              color: factorySaleComparison === "UP" ? "green" : "red",
              marginBottom: "10px",
            }}
          >
            {factorySaleComparison === "UP"
              ? "⬆ Current month sales are higher than last month"
              : "⬇ Current month sales are lower than last month"}
          </p>

          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={monthlyFactorySalesGraph}
              margin={{ top: 20, right: 20, left: 10, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                interval={0}
                angle={-35}
                textAnchor="end"
                height={60}
              />
              <YAxis />
              <Tooltip />
              <Bar dataKey="totalSale" fill="#2563eb" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
      </div>
    </div>
  );
};

const Card = ({ title, value }) => (
  <div className="monthly-card">
    <h4>{title}</h4>
    <p>{value}</p>
  </div>
);

export default Monthly;